/**
 * First-class tenancy — the firm side. The Client Book (a firm's book of clients
 * from the firm-side link index) and the act-as token exchange.
 *
 * These are FIRM-console calls: they MUST run on the firm-user's own Supabase
 * session (tenant = the firm), never on an act-as token — even if a firm-user
 * already has an act-as session open and is switching clients. So every call
 * here passes `useOperatorSession: true`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from './client';

export interface FirmClient {
  clientTenantId: string;
  clientDisplayName: string;
  accessLevel: 'read' | 'manage';
  scope: 'all-trusts' | 'specific-trusts';
  status: 'active' | 'revoked' | 'expired';
  grantId: string;
  /** Valuations Wave 1 (F8) — the client's total value in WHOLE RAND (the
   *  TrustDoc.totalValue convention), rolled up server-side across the client
   *  tenant's trusts. NOT returned by today's GET /v1/firm/clients (checked
   *  2026-07-04 — `firm-clients-list.ts` projects link fields only): typed
   *  optional so the Client Book renders the Value column the moment the
   *  backend adds it, and renders nothing until then. Do NOT compute this
   *  client-side — that would be an N×act-as fan-out. */
  totalValueRand?: number;
  /** ISO date the rolled-up value is "as at". Optional — same backend gap. */
  valueAsAt?: string;
  /** RM scoping (lane C, 2026-07-09) — the SUPABASE user ids of the relationship
   *  managers assigned to this client. NOT returned by today's GET /v1/firm/clients
   *  (checked 2026-07-09 — `firm-clients-list.ts` projects link fields only, same
   *  gap-shape as `totalValueRand`): typed optional so the Client Book renders
   *  assignees the moment the backend adds the projection. In-session, the
   *  `useSetFirmClientAssignees` success handler seeds this field into the query
   *  cache from the mutation response. */
  assignedUserIds?: string[];
  /** Hybrid billing (ruling 2026-09-03) — WHO CARRIES THE BILL for this client,
   *  overriding the practice default. `null` means "follows the practice";
   *  `undefined` means the field has not loaded yet. The distinction is
   *  deliberate and the backend projects an explicit `null` to preserve it:
   *  `assignedUserIds` spent a month indistinguishable between "unassigned" and
   *  "the projection has not shipped", and the Client Book cannot make an
   *  honest billing statement under that ambiguity. */
  billingPayer?: FirmClientPayer | null;
  /** Whether YOUR practice set this workspace up, as opposed to holding a grant
   *  on one that signed itself up or that another practice created (2026-09-03).
   *
   *  It decides whether "my practice carries the bill" may even be offered:
   *  the backend resolver reads the per-client payer override ONLY for a
   *  workspace its firm stewards, so on any other row that setting is dead
   *  storage. The route now refuses it with 409 PAYER_NEEDS_STEWARDSHIP, and
   *  this field is what stops the screen asking in the first place.
   *
   *  `undefined` means NOT STATED, and is deliberately different from `false`:
   *  a backend that predates this field, or one whose tenants read failed, says
   *  nothing rather than "no", and the screen then behaves as it did before.
   *  Guessing `false` would hide a control a firm is entitled to. */
  stewardedByYou?: boolean;
}

export interface FirmClientsResponse {
  clients: FirmClient[];
  correlationId: string;
}

/** The firm's book of clients (scoped server-side: an RM sees only their
 *  assigned clients, an admin sees the whole firm). */
/** Exported for the admin prefetch — the firm's ACTUAL landing surface is
 *  /admin/client-book, so this is the first thing a firm user waits on. */
export const FIRM_CLIENTS_LIST_QUERY_KEY = ['firm-clients', 'list'] as const;

/**
 * Key for the offboarded-INCLUSIVE read (2026-08-22). A deliberate CHILD of the
 * default key, not a separate root: `invalidateQueries({ queryKey:
 * ['firm-clients','list'] })` — which onboard / resign / the offboard modal all
 * fire — matches by prefix and therefore still refreshes both variants, and the
 * admin prefetch keeps seeding the default key untouched.
 */
export const FIRM_CLIENTS_LIST_ALL_QUERY_KEY = ['firm-clients', 'list', 'all'] as const;

/**
 * GET /v1/firm/clients. Returns ACTIVE clients only by default — since
 * 2026-08-22 the endpoint applies the same `status === 'active'` filter every
 * other firm surface uses, so counts agree across screens.
 * `includeRevoked` opts into the full book (active + revoked + expired) for the
 * Client Book's "Show offboarded" view; without it those clients are
 * unreachable in the console.
 */
export function fetchFirmClients(signal?: AbortSignal, opts: { includeRevoked?: boolean } = {}) {
  const path = opts.includeRevoked
    ? '/api/v1/firm/clients?includeRevoked=true'
    : '/api/v1/firm/clients';
  return api<FirmClientsResponse>(path, { signal, useOperatorSession: true });
}

export function useFirmClients(opts: { enabled?: boolean; includeRevoked?: boolean } = {}) {
  const includeRevoked = opts.includeRevoked ?? false;
  return useQuery<FirmClientsResponse>({
    queryKey: includeRevoked ? FIRM_CLIENTS_LIST_ALL_QUERY_KEY : FIRM_CLIENTS_LIST_QUERY_KEY,
    queryFn: ({ signal }) => fetchFirmClients(signal, { includeRevoked }),
    enabled: opts.enabled ?? true,
  });
}

export interface ActAsResponse {
  token: string;
  expiresAt: string;
  accessLevel: 'read' | 'manage';
  clientTenantId: string;
  correlationId: string;
}

export interface OnboardFirmClientInput {
  /** The new client's display name (required). */
  clientDisplayName: string;
  /** Firm's access level over the client — defaults to 'manage'. */
  accessLevel?: 'read' | 'manage';
}

export interface OnboardFirmClientResponse {
  clientTenantId: string;
  grantId: string;
  accessLevel: 'read' | 'manage';
  correlationId: string;
}

/**
 * Onboard a BRAND-NEW client (A2, first-class tenancy). POST /v1/firm/clients
 * mints a CLIENT-OWNED tenant (kind 'b2c', firm-stewarded, no owner yet so the
 * client can self-claim and leave later — they are never vendor-locked to the
 * firm) plus a revocable grant for the calling firm. Firm-console call → runs on
 * the operator's own session. On success the Client Book list is refetched.
 */
export function useOnboardFirmClient() {
  const qc = useQueryClient();
  return useMutation<OnboardFirmClientResponse, ApiError, OnboardFirmClientInput>({
    mutationFn: ({ clientDisplayName, accessLevel }) =>
      api<OnboardFirmClientResponse>('/api/v1/firm/clients', {
        method: 'POST',
        useOperatorSession: true,
        body: { clientDisplayName, ...(accessLevel ? { accessLevel } : {}) },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['firm-clients', 'list'] });
    },
  });
}

// ── RM assignment (lane C · C1, 2026-07-09) ─────────────────────────────────
//
// POST /v1/firm/clients/{clientTenantId}/assignees — idempotent replace of the
// firm-side link's `assignedUserIds` (admin-only server-side; empty array
// unassigns all). Firm-console call → operator session.
//
// ID SHAPE (resolved per Paul's ruling, option A): every backend consumer of
// `assignedUserIds` keys on the SUPABASE user id (`claims.sub` in
// firm-clients-list / firm-act-as / firm-client-offboard). GET /v1/team now
// projects `supabaseUserId` for admin-tier viewers, so the Client Book's
// assignee picker sends those ids (NOT the Cosmos UserDoc id). GET /v1/firm/
// clients now projects `assignedUserIds`, so the row renders current assignees
// + survives reload. (Both are backend list-projection reads — they light up
// once the dev/prod backend carrying them is deployed.)

export interface SetFirmClientAssigneesInput {
  clientTenantId: string;
  /** SUPABASE user ids (see ID SHAPE note above). Empty = unassign all. */
  userIds: string[];
}

export interface SetFirmClientAssigneesResponse {
  clientTenantId: string;
  assignedUserIds: string[];
  correlationId: string;
}

export function useSetFirmClientAssignees() {
  const qc = useQueryClient();
  return useMutation<SetFirmClientAssigneesResponse, ApiError, SetFirmClientAssigneesInput>({
    mutationFn: ({ clientTenantId, userIds }) =>
      api<SetFirmClientAssigneesResponse>(
        `/api/v1/firm/clients/${encodeURIComponent(clientTenantId)}/assignees`,
        { method: 'POST', useOperatorSession: true, body: { userIds } },
      ),
    onSuccess: (res) => {
      // The list endpoint doesn't project assignedUserIds (see FirmClient note),
      // so seed the cache from the authoritative mutation response — assignees
      // render in-session and reconcile automatically once the backend projects
      // the field on reload. setQueriesData (prefix match) so the seed lands on
      // BOTH the default and the includeRevoked variant of the list.
      qc.setQueriesData<FirmClientsResponse>({ queryKey: FIRM_CLIENTS_LIST_QUERY_KEY }, (prev) =>
        prev
          ? {
              ...prev,
              clients: prev.clients.map((c) =>
                c.clientTenantId === res.clientTenantId
                  ? { ...c, assignedUserIds: res.assignedUserIds }
                  : c,
              ),
            }
          : prev,
      );
    },
  });
}

// ── Offboard / resign a client (lane C · C2, 2026-07-09) ────────────────────
//
// POST /v1/firm/clients/{clientTenantId}/resign — the FIRM-initiated
// counterpart to the client-side grant revoke. The firm gives up its own
// access: the authoritative grant flips in the CLIENT tenant + the firm-side
// link, act-as tokens die on their next request (per-request grant re-check),
// and the event is audited in BOTH tenants. The client keeps their tenant and
// every record. Firm-console call → operator session. Already-revoked →
// idempotent 404 GRANT_NOT_FOUND (callers treat it as "already offboarded" and
// refresh the book). Server-side gate: tenant-wide writers, or an `rm` scoped
// to this client.

export interface ResignFirmClientResponse {
  resigned: true;
  clientTenantId: string;
  grantId: string;
  correlationId: string;
}

export function useResignFirmClient() {
  const qc = useQueryClient();
  return useMutation<ResignFirmClientResponse, ApiError, { clientTenantId: string }>({
    mutationFn: ({ clientTenantId }) =>
      api<ResignFirmClientResponse>(
        `/api/v1/firm/clients/${encodeURIComponent(clientTenantId)}/resign`,
        { method: 'POST', useOperatorSession: true },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['firm-clients', 'list'] });
    },
  });
}

/** Exchange the firm token for a short-lived act-as token over a granted client.
 *  Grant-gated server-side (no grant → 404). */
export function useActAs() {
  return useMutation<ActAsResponse, ApiError, { clientTenantId: string }>({
    mutationFn: ({ clientTenantId }) =>
      api<ActAsResponse>(`/api/v1/firm/clients/${clientTenantId}/act-as`, {
        method: 'POST',
        useOperatorSession: true,
      }),
  });
}

// ── Hybrid billing: the per-client payer override (ruling 2026-09-03) ────────
//
// PUT /v1/firm/clients/{clientTenantId}/payer. Beno's case, 2026-08-18: "a firm
// covers some clients while others pay for themselves, per client." The firm
// answers once for the whole practice on the onboarding payment step; this is
// how it says something different about ONE client.
//
// Three answers, and `null` is not a third state: it CLEARS the override so the
// client follows the practice default, now and whenever the practice changes
// it. Admin-only server-side; an offboarded client is a 404.

export type FirmClientPayer = 'firm' | 'client';

export interface SetFirmClientPayerInput {
  clientTenantId: string;
  /** `null` clears the override (follow the practice default). */
  payer: FirmClientPayer | null;
}

export interface SetFirmClientPayerResponse {
  clientTenantId: string;
  billingPayer: FirmClientPayer | null;
  correlationId: string;
}

export function useSetFirmClientPayer() {
  const qc = useQueryClient();
  return useMutation<SetFirmClientPayerResponse, ApiError, SetFirmClientPayerInput>({
    mutationFn: ({ clientTenantId, payer }) =>
      api<SetFirmClientPayerResponse>(
        `/api/v1/firm/clients/${encodeURIComponent(clientTenantId)}/payer`,
        { method: 'PUT', useOperatorSession: true, body: { payer } },
      ),
    onSuccess: (res) => {
      // Seed the authoritative answer into every cached variant of the book so
      // the row reflects the change without a refetch, then let the next read
      // reconcile. Prefix match, so both the default and the includeRevoked
      // list get it (the same shape `useSetFirmClientAssignees` uses).
      qc.setQueriesData<FirmClientsResponse>({ queryKey: FIRM_CLIENTS_LIST_QUERY_KEY }, (prev) =>
        prev
          ? {
              ...prev,
              clients: prev.clients.map((c) =>
                c.clientTenantId === res.clientTenantId
                  ? { ...c, billingPayer: res.billingPayer }
                  : c,
              ),
            }
          : prev,
      );
    },
  });
}
